import React, { useEffect } from 'react';
import './NotFoundPage.css';

function NotFoundPage({ setView }) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.title = '404 - Page Not Found | Experts Communication';
  }, []);

  const handleNavigate = (e, view, path) => {
    if (e) e.preventDefault();
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }
    if (typeof setView === 'function') {
      setView(view);
    } else {
      window.location.href = path;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const destinations = [
    {
      title: 'BPO Services',
      desc: 'Inbound & outbound call center operations, back-office processing, and 24/7 dedicated customer success agents.',
      view: 'bpo-services',
      path: '/bpo-services/',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
          <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
        </svg>
      )
    },
    {
      title: 'Web & App Development',
      desc: 'High-performance responsive full-stack websites, tailored CMS, scalable web portals, and cloud integrations.',
      view: 'web-development',
      path: '/web-development/',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="16 18 22 12 16 6" />
          <polyline points="8 6 2 12 8 18" />
        </svg>
      )
    },
    {
      title: 'Digital Marketing & SEO',
      desc: 'Dominant organic rankings, ROI-focused performance PPC, lead generation, and social media brand positioning.',
      view: 'digital-marketing',
      path: '/digital-marketing/',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      )
    },
    {
      title: 'Managed IT Services',
      desc: 'Robust network infrastructure, server setups, cloud environments, technical maintenance, and cyber monitoring.',
      view: 'it-services',
      path: '/it-services/',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
          <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
          <line x1="6" y1="6" x2="6.01" y2="6" />
          <line x1="6" y1="18" x2="6.01" y2="18" />
        </svg>
      )
    },
    {
      title: 'E-Commerce Solutions',
      desc: 'High-converting custom online storefronts, payment gateways, seamless shopping carts, and inventory automation.',
      view: 'ecommerce-web-development',
      path: '/ecommerce-web-development/',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="9" cy="21" r="1" />
          <circle cx="20" cy="21" r="1" />
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
        </svg>
      )
    },
    {
      title: 'Careers & Opportunities',
      desc: 'Looking to join Pakistan’s fastest-growing IT & BPO firm? Explore open career vacancies and apply today.',
      view: 'careers',
      path: '/careers/',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        </svg>
      )
    }
  ];

  return (
    <div className="ec-404-page">
      {/* Hero Section */}
      <section
        className="ec-404-hero"
        style={{ backgroundImage: "url('/assets/images/cityscape_dark_night.png')" }}
      >
        <div className="ec-404-overlay" />
        <div className="ec-404-glow-orb-1" />
        <div className="ec-404-glow-orb-2" />

        <div className="ec-404-container">
          {/* Top Meta: Breadcrumbs & Status Badge */}
          <div className="ec-404-top-meta">
            <nav className="ec-404-breadcrumbs" aria-label="Breadcrumb">
              <a
                href="/"
                className="ec-404-crumb-link"
                onClick={(e) => handleNavigate(e, 'home', '/')}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                <span>Home</span>
              </a>
              <span className="ec-404-crumb-sep">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </span>
              <span className="ec-404-crumb-current">404 Error</span>
            </nav>

            <div className="ec-404-badge">
              <span className="ec-404-badge-dot" />
              <span>404 &bull; Page Not Found</span>
            </div>
          </div>

          {/* 404 Large Number Display */}
          <div className="ec-404-visual-wrap">
            <h1 className="ec-404-number">404</h1>
            <div className="ec-404-number-glow" aria-hidden="true">404</div>
          </div>

          {/* Headings */}
          <h2 className="ec-404-heading">
            Oops! This Page Got Lost in the Cloud
          </h2>

          <p className="ec-404-description">
            The link you clicked might be outdated, mistyped, or the page may have been moved to a new destination.
            Don't worry &mdash; let's get you back to growing your business with our expert solutions.
          </p>

          {/* Action CTAs */}
          <div className="ec-404-actions">
            <a
              href="/"
              className="ec-404-btn-primary"
              onClick={(e) => handleNavigate(e, 'home', '/')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              <span>Back to Homepage</span>
            </a>

            <a
              href="/services/"
              className="ec-404-btn-secondary"
              onClick={(e) => handleNavigate(e, 'services', '/services/')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
              <span>Explore All Services</span>
            </a>

            <a
              href="/contact/"
              className="ec-404-btn-tertiary"
              onClick={(e) => handleNavigate(e, 'contact', '/contact/')}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <span>Contact Support</span>
            </a>
          </div>
        </div>
      </section>


    </div>
  );
}

export default NotFoundPage;
